import { Box, ToggleButton, ToggleButtonGroup, TextField, Stack, Typography, Paper } from '@mui/material';
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import RuleRoundedIcon from '@mui/icons-material/RuleRounded';
import BlockRoundedIcon from '@mui/icons-material/BlockRounded';
import SectionHeading from './SectionHeading';

const NAVY = '#052F5D';

const OPTIONS = [
    { value: 'fit', label: 'Fit for work', icon: <CheckCircleOutlineRoundedIcon sx={{ fontSize: 18 }} />, accent: 'rgba(46,125,50,0.5)', bg: 'rgba(46,125,50,0.08)' },
    { value: 'modified', label: 'Modified duties', icon: <RuleRoundedIcon sx={{ fontSize: 18 }} />, accent: 'rgba(196,106,0,0.5)', bg: '#FFF1D6' },
    { value: 'unfit', label: 'Unfit for work', icon: <BlockRoundedIcon sx={{ fontSize: 18 }} />, accent: 'rgba(211,47,47,0.45)', bg: 'rgba(211,47,47,0.08)' },
];

export default function WorkStatusTab({ encounter, onPatch, disabled }) {
    const ws = encounter?.workStatus || {};
    const set = (patch) => onPatch({ workStatus: { ...ws, ...patch } });

    return (
        <Box>
            <SectionHeading
                title="Work status"
                hint="Determines what goes on the patient's fit-for-work certificate."
            />

            <ToggleButtonGroup
                exclusive
                value={ws.status || null}
                onChange={(_, v) => v && !disabled && set({ status: v })}
                sx={{
                    flexWrap: 'wrap',
                    gap: 1,
                    '& .MuiToggleButton-root': {
                        textTransform: 'none',
                        fontWeight: 500,
                        px: 2,
                        py: 1,
                        borderRadius: '12px !important',
                        border: '1px solid rgba(5,47,93,0.15)',
                        color: 'text.secondary',
                        gap: 0.75,
                    },
                }}
            >
                {OPTIONS.map((o) => (
                    <ToggleButton
                        key={o.value}
                        value={o.value}
                        disabled={disabled}
                        sx={{
                            '&.Mui-selected': {
                                color: NAVY,
                                bgcolor: o.bg,
                                borderColor: o.accent,
                                fontWeight: 600,
                                '&:hover': { bgcolor: o.bg },
                            },
                        }}
                    >
                        {o.icon}
                        {o.label}
                    </ToggleButton>
                ))}
            </ToggleButtonGroup>

            <Paper variant="outlined" sx={{ p: 2, mt: 2.5, borderRadius: 1.5, borderColor: 'rgba(5,47,93,0.10)' }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <TextField
                        label="Days off"
                        type="number"
                        size="small"
                        disabled={disabled}
                        value={ws.daysOff ?? ''}
                        onChange={(e) => set({ daysOff: e.target.value === '' ? undefined : Number(e.target.value) })}
                        sx={{ width: { xs: '100%', sm: 160 } }}
                    />
                    <TextField
                        label="Return-to-work date"
                        type="date"
                        size="small"
                        InputLabelProps={{ shrink: true }}
                        disabled={disabled}
                        value={ws.returnDate ? String(ws.returnDate).slice(0, 10) : ''}
                        onChange={(e) => set({ returnDate: e.target.value })}
                        sx={{ width: { xs: '100%', sm: 220 } }}
                    />
                </Stack>
                <TextField
                    label="Restrictions / notes"
                    multiline
                    minRows={3}
                    fullWidth
                    disabled={disabled}
                    value={ws.restrictions || ''}
                    onChange={(e) => set({ restrictions: e.target.value })}
                    sx={{ mt: 2 }}
                />
            </Paper>
        </Box>
    );
}
